import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, User, Search, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { logout } from '../services/api';
import { toast } from 'sonner';

export default function Navbar({ user, onLogout }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      if (onLogout) onLogout();
      toast.success('Logged out successfully');
      navigate('/');
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <nav className="sticky top-0 z-50 glass-nav border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo-link">
            <img src="/afyagram_logo.png" alt="Afyagram" className="h-12 w-auto" />
          </Link>

          {/* Always Visible Navigation Items */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* All Doctors - Always visible */}
            <Link to="/all-doctors">
              <Button
                data-testid="nav-all-doctors-btn"
                variant="ghost"
                size="sm"
                className="text-[#262626] hover:text-[#0F2A44] hover:bg-slate-100 text-xs sm:text-sm"
              >
                All Doctors
              </Button>
            </Link>

            {/* All Facilities - Always visible */}
            <Link to="/facilities">
              <Button
                data-testid="nav-facilities-btn"
                variant="ghost"
                size="sm"
                className="text-[#262626] hover:text-[#0F2A44] hover:bg-slate-100 text-xs sm:text-sm"
              >
                Facilities
              </Button>
            </Link>
            
            {/* Search Icon - Always visible */}
            <Link to="/directory">
              <Button
                data-testid="nav-search-btn"
                variant="ghost"
                size="sm"
                className="text-[#0F2A44] hover:bg-slate-100 p-2"
              >
                <Search className="h-5 w-5" />
              </Button>
            </Link>

            {/* Desktop Only - Additional Items */}
            <div className="hidden md:flex items-center gap-4">
              {!user || user.role === 'patient' ? (
                <>
                  <Link to="/doctor/register">
                    <Button
                      data-testid="nav-enlist-btn"
                      variant="ghost"
                      size="sm"
                      className="text-[#2DD4BF] hover:text-[#2DD4BF]/80 hover:bg-[#2DD4BF]/10 font-medium"
                    >
                      Enlist as Doctor
                    </Button>
                  </Link>
                  <Link to="/facility/register">
                    <Button
                      data-testid="nav-facility-btn"
                      variant="ghost"
                      size="sm"
                      className="text-[#4A90E2] hover:text-[#4A90E2]/80 hover:bg-[#4A90E2]/10 font-medium"
                    >
                      Enlist Facility
                    </Button>
                  </Link>
                </>
              ) : null}

              {user ? (
                <>
                  <Link to={
                    ['super_admin', 'admin', 'moderator', 'custom'].includes(user.role) ? '/admin/dashboard' :
                    user.role === 'doctor' ? '/doctor/dashboard' :
                    '/patient/dashboard'
                  }>
                    <Button
                      data-testid="nav-dashboard-btn"
                      variant="ghost"
                      size="sm"
                      className="text-[#262626] hover:text-[#0F2A44] hover:bg-slate-100"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button
                    data-testid="nav-logout-btn"
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="text-[#262626] hover:text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <Link to="/login">
                  <Button
                    data-testid="nav-login-btn"
                    className="bg-[#0F2A44] text-white hover:bg-[#0F2A44]/90 rounded-full px-6"
                  >
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Hamburger Menu - For extra items only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-[#0F2A44] hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Only extra items */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4">
            <div className="flex flex-col space-y-2">
              {!user || user.role === 'patient' ? (
                <>
                  <Link to="/doctor/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-[#2DD4BF] hover:text-[#2DD4BF]/80 hover:bg-[#2DD4BF]/10 font-medium"
                    >
                      Enlist as Doctor
                    </Button>
                  </Link>
                  <Link to="/facility/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-[#4A90E2] hover:text-[#4A90E2]/80 hover:bg-[#4A90E2]/10 font-medium"
                    >
                      Enlist Facility
                    </Button>
                  </Link>
                </>
              ) : null}

              {user ? (
                <>
                  <Link 
                    to={
                      ['super_admin', 'admin', 'moderator', 'custom'].includes(user.role) ? '/admin/dashboard' :
                      user.role === 'doctor' ? '/doctor/dashboard' :
                      '/patient/dashboard'
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-[#262626] hover:text-[#0F2A44] hover:bg-slate-100"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="w-full justify-start text-[#262626] hover:text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#0F2A44] text-white hover:bg-[#0F2A44]/90 rounded-full">
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}